import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { BackboneCanvas } from "./BackboneCanvas";
import { useThemeMode } from "./Theme";
import { APP_URL } from "./data";

const LINES = [["Read", "the", "backbone."], ["Keep", "the", "evidence"], ["honest."]];
const ease = [0.16, 1, 0.3, 1];

const MaskLine = ({ words, i }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease }}
    >
      {words.map((w, j) => (
        <span key={j} className={i === 2 ? "text-[var(--amber)] italic" : ""}>{w}{j < words.length - 1 ? " " : ""}</span>
      ))}
    </motion.span>
  </span>
);

const Tag = ({ children, className, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.8, delay, ease }}
    className={`absolute font-mono text-[11px] sm:text-xs rounded-md border border-[var(--line)] bg-[var(--glass)] backdrop-blur-md px-3 py-1.5 text-[var(--muted)] ${className}`}
  >
    {children}
  </motion.div>
);

const RunCard = () => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay: 2.0, ease }}
    data-testid="hero-run-card"
    className="absolute left-[4%] top-[2%] sm:top-auto sm:left-auto sm:right-[4%] sm:bottom-[4%] lg:right-auto lg:left-[6%] lg:bottom-[22%] w-[230px] sm:w-[270px] rounded-lg border border-[var(--line)] bg-[var(--glass)] backdrop-blur-md p-4 shadow-[0_20px_50px_-20px_rgba(17,25,23,0.25)]"
  >
    <div className="flex items-center justify-between">
      <span className="font-display font-bold">HRAS <span className="font-mono text-xs font-normal text-[var(--muted)]">WT 5P21</span></span>
      <span className="rounded border border-[#1f7a6c]/30 bg-[#1f7a6c]/10 px-1.5 py-0.5 font-mono text-[10px] text-[var(--teal)]">done</span>
    </div>
    <p className="mt-1 font-mono text-[11px] text-[var(--muted)]">253/253 completed</p>
    <div className="tri-bar mt-3 h-1.5 rounded-full" />
    <div className="mt-3 grid grid-cols-3 gap-2 font-mono text-[11px]">
      <span><b className="block font-display text-base text-[var(--teal)]">94</b>near</span>
      <span><b className="block font-display text-base text-[var(--amber)]">10</b>moderate</span>
      <span><b className="block font-display text-base text-[var(--rose)]">1</b>large</span>
    </div>
  </motion.div>
);

export const Hero = () => {
  const { dark } = useThemeMode();
  const { scrollY } = useScroll();
  const yCanvas = useTransform(scrollY, [0, 800], [0, 160]);
  const yText = useTransform(scrollY, [0, 800], [0, -60]);

  return (
    <section id="top" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden hairline-grid">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(31,122,108,0.12),transparent_60%)] dark:bg-[radial-gradient(ellipse_at_70%_40%,rgba(31,122,108,0.28),transparent_60%)]" />
      <motion.div style={{ y: yCanvas }} className="absolute inset-x-0 bottom-0 h-[42vh] lg:bottom-auto lg:top-0 lg:left-[44%] lg:h-full">
        <BackboneCanvas dark={dark} />
        <Tag className="hidden sm:block right-[12%] top-[14%]" delay={1.4}>Cα–Cα target ≈ 3.80 Å</Tag>
        <Tag className="hidden sm:block right-[4%] sm:top-[58%] text-[var(--amber)]" delay={1.6}>flagged node · deviation</Tag>
        <Tag className="hidden sm:block right-[8%] bottom-[24%]" delay={1.8}>Kabsch-aligned · baseline frame</Tag>
        <RunCard />
      </motion.div>

      <motion.div style={{ y: yText }} className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-32 sm:pt-40 lg:pt-48 pb-[46vh] lg:pb-24 pointer-events-none">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2, duration: 1 }}
          className="font-mono text-xs uppercase tracking-[0.22em] text-[var(--teal)]">
          Morphogenesis Analytics — protein-structure evidence workbench
        </motion.p>
        <h1 data-testid="hero-title" className="font-display mt-6 text-[13vw] sm:text-7xl lg:text-[7.5rem] font-extrabold tracking-[-0.04em] leading-[0.9]">
          {LINES.map((w, i) => <MaskLine key={i} words={w} i={i} />)}
        </h1>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.0, duration: 1, ease }} className="pointer-events-auto">
          <p className="mt-8 max-w-md text-base md:text-lg text-[var(--muted)] leading-relaxed">
            Audit Cα geometry, compare structures, run 60 oncology gene families and triage named substitutions —
            without ever letting geometry stand in for pathogenicity, or a label stand in for sequence.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <a href="#contact" data-testid="hero-request-access-btn"
              className="group inline-flex items-center gap-2 rounded-md bg-[var(--teal)] px-6 py-3.5 font-semibold text-[var(--on-teal)] shadow-[0_10px_30px_-10px_rgba(31,122,108,0.6)] hover:bg-[var(--text)] transition-colors">
              Get in touch <ArrowDownRight className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
            </a>
            <a href={APP_URL} target="_blank" rel="noreferrer" data-testid="hero-launch-app-btn"
              className="group inline-flex items-center gap-2 rounded-md border border-[var(--line)] bg-[var(--glass)] backdrop-blur px-6 py-3.5 font-semibold hover:border-[var(--teal)] hover:text-[var(--teal)] transition-colors">
              Open the workbench <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </a>
          </div>
          <a href="#explainer" data-testid="hero-explainer-link" className="mt-5 inline-block text-sm text-[var(--muted)] underline decoration-[var(--line)] underline-offset-4 hover:text-[var(--teal)] hover:decoration-[var(--teal)] transition-colors">New here? Read the plain-English version →</a>
        </motion.div>
      </motion.div>

      <div className="hidden sm:flex absolute bottom-6 inset-x-0 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--muted)]">
        <span>RCSB · PDBe · PDBj · UniProt · AlphaMissense</span>
        <span className="hidden sm:inline">Scroll ↓</span>
      </div>
    </section>
  );
};
